import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Box, Switch, List, ListItem, ListItemText, useMediaQuery } from '@mui/material';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material';
import DetailHeader from './components/DetailHeader.js';
import { useNavigation } from './App.js';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase.js';

function IngredientDetail({ inventory, recipes, togglePossession, ingredients, user }) {
  const { ingredientId } = useParams();
  const location = useLocation();
  const { getPreviousPage } = useNavigation();
  const [ingredient, setIngredient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const loadIngredient = async () => {
      try {
        // First check in the main ingredients collection
        const foundIngredient = ingredients.find(ing => ing.id === ingredientId);
        if (foundIngredient) {
          setIngredient(foundIngredient);
          setLoading(false);
          return;
        }

        // If not found in main collection and user is logged in, check user's ingredients
        if (user) {
          const userIngredientRef = doc(db, `users/${user.uid}/ingredients`, ingredientId);
          const userIngredientDoc = await getDoc(userIngredientRef);
          if (userIngredientDoc.exists()) {
            setIngredient({ id: userIngredientDoc.id, ...userIngredientDoc.data() });
            setLoading(false);
            return;
          }
        }

        setIngredient(null);
        setLoading(false);
      } catch (error) {
        console.error('Error loading ingredient:', error);
        setIngredient(null);
        setLoading(false);
      }
    };

    loadIngredient();
  }, [ingredientId, ingredients, user]);

  const recipesUsingIngredient = useMemo(() => {
    return recipes.filter(recipe => 
      recipe.ingredients.some(i => i.ingredientId === ingredient?.id)
    );
  }, [recipes, ingredient?.id]);

  const currentInventory = useMemo(() => {
    return inventory.find(ing => ing.id === ingredient?.id) || { possessed: false };
  }, [inventory, ingredient?.id]);

  const previousPage = getPreviousPage(location.pathname);
  const from = previousPage?.from || location.state?.from;

  let backLink, backLabel;
  if (from && from.type && from.id) {
    if (from.type === 'recipe') {
      const prevRecipe = recipes.find(r => r.id === from.id);
      backLink = `/recipe/${from.id}`;
      backLabel = prevRecipe ? prevRecipe.name : 'Recipe';
    } else if (from.type === 'ingredient') {
      const prevIngredient = ingredients.find(i => i.id === from.id);
      backLink = `/ingredient/${from.id}`;
      backLabel = prevIngredient ? prevIngredient.name : 'Ingredient';
    } else if (from.type === 'menu') {
      const menu = inventory.find(m => m.id === from.id); // Adjust if menus are passed separately
      backLink = `/cocktail-menu/${from.id}`;
      backLabel = menu ? menu.name : 'Cocktail Menu';
    } else {
      backLink = '/recipes';
      backLabel = 'Recipes';
    }
  } else {
    backLink = '/recipes';
    backLabel = 'Recipes';
  }

  const headerActions = (
    <Switch
      checked={currentInventory.possessed}
      onChange={() => togglePossession(ingredient.id)}
      color="primary"
      size={isMobile ? "medium" : "small"}
    />
  );

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  if (!ingredient) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="h6">Ingredient not found</Typography>
      </Box>
    );
  }

  const showImage = ingredient.image && !ingredient.image.includes("example.com") && !imageError;

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3 },
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary
    }}>
      <DetailHeader 
        title={ingredient.name}
        actions={headerActions}
        backLink={backLink}
        backLabel={backLabel}
      />
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 3, md: 4 }
      }}>
        {showImage && (
          <Box sx={{ 
            width: { xs: '100%', md: '300px' },
            height: { xs: '250px', md: '300px' },
            flexShrink: 0,
            borderRadius: { xs: 2, sm: 1 },
            overflow: 'hidden',
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box
              component="img"
              src={ingredient.image}
              alt={ingredient.name}
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              onError={() => setImageError(true)}
            />
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" paragraph sx={{ fontSize: { xs: '1rem', sm: 'inherit' }, lineHeight: { xs: 1.6, sm: 'inherit' } }}>
            {ingredient.description}
          </Typography>
          <Box sx={{ 
            backgroundColor: theme.palette.background.paper,
            p: { xs: 2, sm: 1.5 },
            borderRadius: { xs: 2, sm: 1 },
            mt: 2
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary', fontSize: { xs: '1.1rem', sm: '1rem' } }}>
              Used in {recipesUsingIngredient.length} {recipesUsingIngredient.length === 1 ? 'recipe' : 'recipes'}:
            </Typography>
            {recipesUsingIngredient.length > 0 && (
              <List dense={!isMobile}>
                {recipesUsingIngredient.map(recipe => {
                  const displayName = recipes.filter(r => r.name === recipe.name).length > 1 
                    ? `${recipe.name} (${recipe.origin})` 
                    : recipe.name;
                  return (
                    <ListItem 
                      key={recipe.id} 
                      sx={{ 
                        py: { xs: 1.5, sm: 0.5 },
                        px: { xs: 1, sm: 2 },
                        '&:hover': { backgroundColor: theme.palette.action.hover, borderRadius: { xs: 2, sm: 1 } },
                        '&:active': { backgroundColor: theme.palette.action.selected },
                        '& a': {
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          fontSize: { xs: '1rem', sm: '0.875rem' },
                          fontWeight: isMobile ? 500 : 400,
                          display: 'block',
                          width: '100%',
                          '&:hover': { textDecoration: 'underline' },
                          '&:visited': { color: theme.palette.primary.main }
                        }
                      }}
                    >
                      <ListItemText>
                        <Link 
                          to={`/recipe/${recipe.id}`}
                          state={{ from: { type: 'ingredient', id: ingredient.id } }}
                        >
                          {displayName}
                        </Link>
                      </ListItemText>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default IngredientDetail;